import { LoginResponse } from "@/features/auth/services/AuthService";
// src/api/HttpClient.ts
import {
  Observable,
  BehaviorSubject,
  throwError,
  defer,
  of,
  EMPTY,
} from "rxjs";
import { message } from "antd";
import { ajax, AjaxError, AjaxResponse } from "rxjs/ajax";
import {
  catchError,
  finalize,
  map,
  shareReplay,
  switchMap,
} from "rxjs/operators";
import type { RequestOptions } from "./types"; // keep your existing type
import { buildRequestUrl } from "./HttpHelper"; // keep your existing helper
import { APIHosts } from "@/services/reduxHelpers";
import AuthService from "@/features/auth/services/AuthService";
import { decodeJwtToken, saveUserProfileFromToken } from "@/utils/jwtHelper";
import i18n from "@/locales/i18n";

/* ===== Error handler for HTTP status codes ===== */
export const handleHttpError = (err: AjaxError): string => {
  let userMessage = i18n.t("errors:http_default");

  if (err.status === 400)
    userMessage = i18n.t("errors:http_400");
  else if (err.status === 401)
    userMessage = i18n.t("errors:http_401");
  else if (err.status === 403)
    userMessage = i18n.t("errors:http_403");
  else if (err.status === 404)
    userMessage = i18n.t("errors:http_404");
  else if (err.status === 409)
    userMessage = i18n.t("errors:http_409");
  else if (err.status === 429)
    userMessage = i18n.t("errors:http_429");
  return userMessage;
};

export class HttpMethod {
  static GET = "GET";
  static POST = "POST";
  static PUT = "PUT";
  static PATCH = "PATCH";
  static DELETE = "DELETE";
}

// Optional Redux store injection (kept for compatibility)
export type StoreLike = unknown;
let __storeRef: StoreLike | null = null;
let __bootstrapConfig: any | null = null;

/** Allows code to pass in the Redux store; currently unused but kept for API compatibility. */
export const injectStore = (reduxStore: StoreLike) => {
  __storeRef = reduxStore;
};

export const setBootstrapConfig = (cfg: any | null) => {
  __bootstrapConfig = cfg;
};

export const getBootstrapConfig = (): any | null => {
  return __bootstrapConfig;
};

/* ===== token storage ===== */
export const ACCESS_KEY = "auth.accessToken";
export const REFRESH_KEY = "auth.refreshToken";
export const REFRESH_TOKEN_TTL_24H_MS = 24 * 60 * 60 * 1000;
export const REFRESH_TOKEN_STORED_AT_KEY = "auth.refreshTokenStoredAt";

const getStoredAccess = () =>
  localStorage.getItem(ACCESS_KEY) ||
  localStorage.getItem("accessToken") ||
  localStorage.getItem("token");
const setStoredAccess = (t: string | null) =>
  t ? localStorage.setItem(ACCESS_KEY, t) : localStorage.removeItem(ACCESS_KEY);
const getStoredRefresh = () =>
  localStorage.getItem(REFRESH_KEY) || localStorage.getItem("refreshToken");
export const setStoredRefresh = (t: string | null) => {
  if (t) {
    localStorage.setItem(REFRESH_KEY, t);
    localStorage.setItem(REFRESH_TOKEN_STORED_AT_KEY, `${Date.now()}`);
    return;
  }
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(REFRESH_TOKEN_STORED_AT_KEY);
};

const getRefreshTokenStoredAt = (): number | null => {
  const raw = localStorage.getItem(REFRESH_TOKEN_STORED_AT_KEY);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const isStoredRefreshTokenExpired = (): boolean => {
  const refreshToken = getStoredRefresh();
  if (!refreshToken) return true;

  const storedAt = getRefreshTokenStoredAt();
  if (storedAt != null) {
    return Date.now() >= storedAt + REFRESH_TOKEN_TTL_24H_MS;
  }

  const payload = decodeJwtToken(refreshToken);
  if (payload?.exp) {
    return payload.exp * 1000 <= Date.now();
  }

  return false;
};

/* ===== optional refresh endpoint (leave empty to disable) ===== */
const AUTH_REFRESH_URL = `${APIHosts.Authen}/connect/token`; // e.g. `${APIHosts.Authen}/api/auth/refresh`
type RefreshResponse = { accessToken: string; refreshToken?: string };

/* ===== token subject ===== */
export const tokenSubject = new BehaviorSubject<string | null>(
  getStoredAccess()
);
let refreshRequest$: Observable<LoginResponse> | null = null;

const ACCESS_TOKEN_EXPIRE_SKEW_MS = 30_000;
const isTokenExpiredOrMissing = (token: string | null): boolean => {
  if (!token) return true;
  const payload = decodeJwtToken(token);
  if (!payload?.exp) return true;
  return payload.exp * 1000 <= Date.now() + ACCESS_TOKEN_EXPIRE_SKEW_MS;
};

const isPublicUrl = (url: string): boolean => {
  const normalized = url.toLowerCase();
  return normalized.includes("/connect/token");
};

/* expose to login/logout */
export const setToken = (token: string | null) => {
  setStoredAccess(token);
  tokenSubject.next(token);
};

export const isAuthenticated = (): boolean => {
  const token = getStoredAccess();
  return token !== null && token !== "";
};

/* ===== core sender (overloads) ===== */
function sendHttpRequest<T = unknown>(
  url: string,
  options: RequestOptions
): Observable<T>;
function sendHttpRequest<T = unknown>(
  url: string,
  options: RequestOptions,
  isFull: true
): Observable<AjaxResponse<T>>;
function sendHttpRequest<T = unknown>(
  url: string,
  options: RequestOptions,
  isFull?: false
): Observable<T>;
function sendHttpRequest<T = unknown>(
  url: string,
  options: RequestOptions,
  isFull = false
): Observable<T | AjaxResponse<T>> {
  const {
    method = HttpMethod.GET,
    body,
    headers: customHeaders,
    createXHR,
    responseType = "json",
    publicApi = false,
  } = options || {};

  const token = tokenSubject.getValue();

  // Set Accept header based on responseType
  const acceptHeader = responseType === "blob" ? "*/*" : "application/json";

  const base: Record<string, string> = {
    Accept: acceptHeader,
    ...(!publicApi && token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const headers =
    body instanceof FormData
      ? { ...base, ...(customHeaders || {}) } // let browser set boundary
      : {
          "Content-Type": "application/json",
          ...base,
          ...(customHeaders || {}),
        };

  const payload =
    body instanceof FormData
      ? body
      : body == null
      ? undefined
      : typeof body === "string"
      ? body
      : JSON.stringify(body);

  return ajax<T>({
    url,
    method,
    headers,
    body: payload as any,
    responseType: responseType as any,
    ...(createXHR ? { createXHR } : {}),
  }).pipe(map((x: AjaxResponse<T>) => (isFull ? x : (x.response as T))));
}

/* ===== refresh on 401 ===== */
export const refreshAccessToken = (): Observable<LoginResponse> => {
  if (refreshRequest$) {
    return refreshRequest$;
  }

  const refreshToken = getStoredRefresh();
  if (!refreshToken) {
    console.error("No refresh token found. Redirecting to login...");
    message.error(i18n.t("errors:session_expired"));
    window.location.href = "/login";
    return EMPTY;
  }
  if (isStoredRefreshTokenExpired()) {
    setToken(null);
    setStoredRefresh(null);
    localStorage.clear();
    sessionStorage.clear();
    console.error("Refresh token exceeded 24h TTL. Redirecting to login...");
    message.error(i18n.t("errors:session_expired"));
    window.location.href = "/login";
    return EMPTY;
  }

  const oAuthConfig = getBootstrapConfig()?.oAuth ?? {};
  const orgId = localStorage.getItem("orgId") || "";
  const clientId =
    localStorage.getItem("clientId") ||
    oAuthConfig.clientId ||
    "CXM";
  const scope = oAuthConfig.scope || "offline_access API";
  const clientSecret = oAuthConfig.clientSecret || "";

  const refreshBody = new URLSearchParams({
    grant_type: "refresh_token",
    scope,
    client_id: clientId,
    refresh_token: refreshToken,
    remember: "true",
  });
  if (orgId) refreshBody.set("orgId", orgId);
  if (clientSecret) refreshBody.set("client_secret", clientSecret);
  const body = refreshBody.toString();

  refreshRequest$ = ajax({
    url: `${AuthService.config.authenUrl}/connect/token`,
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json",
    },
    body,
    responseType: "json",
  }).pipe(
    map((res: AjaxResponse<any>) => {
      const accessToken = res.response.access_token || res.response.accessToken;
      const refreshToken = res.response.refresh_token || res.response.refreshToken;

      setToken(accessToken);
      setStoredRefresh(refreshToken);
      localStorage.setItem("accessToken", accessToken);
      if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
      saveUserProfileFromToken(accessToken);

      return {
        accessToken,
        refreshToken,
      };
    }),
    catchError((error: AjaxError) => {
      if (error.status === 401 || error.status === 400) {
        setToken(null);
        setStoredRefresh(null);
        localStorage.clear();
        sessionStorage.clear();
        console.error("Refresh token expired. Redirecting to login...");
        message.error(i18n.t("errors:session_expired"));
        window.location.href = "/login";
        return EMPTY;
      }
      console.error("Refresh token error:", error);
      handleHttpError(error);
      message.error(i18n.t("errors:session_expired"));
      window.location.href = "/login";
      return EMPTY;
    }),
    finalize(() => {
      refreshRequest$ = null;
    }),
    shareReplay(1)
  );

  return refreshRequest$;
};

export const ensureValidAccessToken = (): Observable<string | null> => {
  const currentToken = tokenSubject.getValue() || getStoredAccess();

  if (currentToken && !isTokenExpiredOrMissing(currentToken)) {
    if (tokenSubject.getValue() !== currentToken) {
      tokenSubject.next(currentToken);
    }
    return of(currentToken);
  }

  const refreshToken = getStoredRefresh();
  if (!refreshToken || isStoredRefreshTokenExpired()) {
    if (refreshToken && isStoredRefreshTokenExpired()) {
      setToken(null);
      setStoredRefresh(null);
      localStorage.clear();
      sessionStorage.clear();
      message.error(i18n.t("errors:session_expired"));
      window.location.href = "/login";
    }
    return of(currentToken ?? null);
  }

  return refreshAccessToken().pipe(
    map((res) => res?.accessToken || tokenSubject.getValue() || null)
  );
};

/* ===== high-level wrapper (overloads) ===== */
function httpRequest<T = unknown>(
  url: string,
  options?: RequestOptions
): Observable<T>;
function httpRequest<T = unknown>(
  url: string,
  options: RequestOptions & { isFull: true }
): Observable<AjaxResponse<T>>;
function httpRequest<T = unknown>(
  url: string,
  options: RequestOptions = {}
): Observable<T | AjaxResponse<T>> {
  const rUrl = buildRequestUrl(url, options.search);
  const full = options.isFull === true;
  const bypassAuth = options.publicApi === true || isPublicUrl(rUrl);

  return defer(() => (bypassAuth ? of(null) : ensureValidAccessToken())).pipe(
    switchMap(() =>
      full
        ? sendHttpRequest<T>(rUrl, { ...options, publicApi: bypassAuth }, true as const)
        : sendHttpRequest<T>(rUrl, { ...options, publicApi: bypassAuth })
    ),
    catchError((error: AjaxError) => {
      if (error.status === 401) {
        setToken(null);
        setStoredRefresh(null);
        localStorage.clear();
        sessionStorage.clear();
        console.error("401 Unauthorized - Redirecting to login");
        message.error(i18n.t("errors:session_expired"));
        setTimeout(() => {
          window.location.href = "/login";
        }, 1000);
        return EMPTY;
      }
      handleHttpError(error);
      return throwError(() => error);
    })
  );
}

/* ===== public API ===== */
class HttpInterceptor {
  request<T = unknown>(
    method: string,
    url: string,
    body?: any,
    options?: RequestOptions
  ) {
    return httpRequest<T>(url, { ...options, method, body });
  }
  get<T = unknown>(url: string, options?: RequestOptions) {
    return this.request<T>(HttpMethod.GET, url, undefined, options);
  }
  post<T = unknown>(url: string, body?: any, options?: RequestOptions) {
    return this.request<T>(HttpMethod.POST, url, body, options);
  }
  put<T = unknown>(url: string, body?: any, options?: RequestOptions) {
    return this.request<T>(HttpMethod.PUT, url, body, options);
  }
  patch<T = unknown>(url: string, body?: any, options?: RequestOptions) {
    return this.request<T>(HttpMethod.PATCH, url, body, options);
  }
  delete<T = unknown>(url: string, body?: any, options?: RequestOptions) {
    return this.request<T>(HttpMethod.DELETE, url, body, options);
  }

  // upload overload A: files + extra data
  upload<T = unknown>(
    url: string,
    files: File[],
    additionalData: any = {},
    options?: RequestOptions
  ): Observable<T>;
  // upload overload B: (legacy) progress callback + FormData
  upload<T = unknown>(
    url: string,
    setProgress: (progress: number) => void,
    formData?: FormData,
    options?: RequestOptions
  ): Observable<T>;
  upload<T = unknown>(
    url: string,
    a: any,
    b?: any,
    options?: RequestOptions
  ): Observable<T> {
    // legacy signature: (url, setProgress, formData?, options?)
    if (typeof a === "function") {
      const setProgress = a as (n: number) => void;
      const formData = (b as FormData) ?? new FormData();
      const ro: RequestOptions = {
        ...options,
        createXHR: () => {
          const xhr = new XMLHttpRequest();
          xhr.upload.onprogress = (evt) => {
            if (evt.lengthComputable)
              setProgress((evt.loaded / evt.total) * 100);
          };
          return xhr;
        },
      };
      return this.post<T>(url, formData, ro);
    }

    // new signature: (url, files, additionalData?, options?)
    const files: File[] = a as File[];
    const extra = b ?? {};
    const form = new FormData();
    for (const f of files ?? []) form.append("Photos", f, f.name);
    for (const key in extra)
      if (extra[key] != null) form.append(key, extra[key]);
    return this.post<T>(url, form, { ...options });
  }
}

const HttpClient = new HttpInterceptor();
export default HttpClient;
